import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Review {
  id: string;
  rating: number;
  comment: string;
  created_at: string;
  reviewer_name?: string;
}

const ReviewList = ({ sellerId }: { sellerId: string }) => {
  const [reviews, setReviews] = useState<Review[]>([]);

  useEffect(() => {
    const fetchReviews = async () => {
      const { data } = await supabase
        .from("reviews")
        .select("*")
        .eq("seller_id", sellerId)
        .order("created_at", { ascending: false })
        .limit(10);

      if (data && data.length > 0) {
        const reviewerIds = [...new Set(data.map((r) => r.reviewer_id))];
        const { data: reviewers } = await supabase
          .from("profiles_public")
          .select("*")
          .in("user_id", reviewerIds);
        const reviewerMap = Object.fromEntries((reviewers ?? []).map((p) => [p.user_id, p]));

        setReviews(data.map((r: any) => ({
          id: r.id,
          rating: r.rating,
          comment: r.comment,
          created_at: r.created_at,
          reviewer_name: reviewerMap[r.reviewer_id]?.display_name || "Acheteur",
        })));
      }
    };
    fetchReviews();
  }, [sellerId]);

  if (reviews.length === 0) return null;

  const avg = (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="font-display font-semibold">Avis vendeur</h3>
        <div className="flex items-center gap-1 text-sm">
          <Star className="h-4 w-4 text-amber" fill="currentColor" />
          <span className="font-medium">{avg}</span>
          <span className="text-muted-foreground">({reviews.length} avis)</span>
        </div>
      </div>
      <div className="space-y-3">
        {reviews.map((r) => (
          <div key={r.id} className="bg-card rounded-lg border p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className={`h-3.5 w-3.5 ${s <= r.rating ? "text-amber fill-current" : "text-muted"}`} />
                ))}
              </div>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs font-medium">{r.reviewer_name}</span>
              <span className="text-xs text-muted-foreground">
                {new Date(r.created_at).toLocaleDateString("fr-BE")}
              </span>
            </div>
            {r.comment && <p className="text-sm text-muted-foreground">{r.comment}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReviewList;
